import { FC, Fragment, useEffect, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { actions as appActions } from '@/api/app';

import { Button } from '@payaca/ambrosia';

import { useSelector } from '@/api/state';
import UntitledIcon from '@payaca/untitled-icons';
import './MaintenanceBanner.sass';

const MaintenanceBanner: FC = (): JSX.Element | null => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(appActions.requestGetMaintenance());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const maintenanceSchedule = useSelector(
    (state: any) => state.app.maintenanceSchedule
  );

  const hideMaintenanceBanner = useSelector(
    (state: any) => state.app.hideMaintenanceBanner
  );
  const lastReadFutureMaintenaceMessageDownTimeAt = useSelector(
    (state: any) => state.app.lastReadFutureMaintenaceMessageDownTimeAt
  );
  const lastReadPastMaintenanceUpTimeAt = localStorage.getItem(
    'payaca-pastMaintenaceMessageUpTimeAt'
  );

  const hasUnreadPastMessage = useMemo(() => {
    if (!maintenanceSchedule?.past) {
      return false;
    }
    // show past maintenace message if user has not closed banner with it in (stored in local storage)
    return (
      maintenanceSchedule.past.upTimeAt !== lastReadPastMaintenanceUpTimeAt
    );
  }, [lastReadPastMaintenanceUpTimeAt, maintenanceSchedule]);

  const canHideFutureMessage = useMemo(() => {
    if (!maintenanceSchedule?.future) {
      return true;
    }
    // can hide future maintenance message if user has seen it and closed it (saved to store)
    return (
      maintenanceSchedule.future.downTimeAt ===
      lastReadFutureMaintenaceMessageDownTimeAt
    );
  }, [lastReadFutureMaintenaceMessageDownTimeAt, maintenanceSchedule]);

  // user has closed banner, future message has been read and no unread past message
  const hideBanner = useMemo(() => {
    return (
      hideMaintenanceBanner && canHideFutureMessage && !hasUnreadPastMessage
    );
  }, [canHideFutureMessage, hasUnreadPastMessage, hideMaintenanceBanner]);

  // no unread past message and no future message
  if (!hasUnreadPastMessage && !maintenanceSchedule?.future) {
    return null;
  }

  return (
    <div className={`maintenance-banner${hideBanner ? ' hidden' : ''}`}>
      {maintenanceSchedule?.future && (
        <span
          className="hammer-icon-wrapper"
          onClick={() => dispatch(appActions.hideMaintenanceBanner(false))}
        >
          <UntitledIcon name="tool-01.3" className="hammer-icon" />
        </span>
      )}
      {!hideBanner && (
        <Fragment>
          <div className="inner-banner-wrapper">
            {hasUnreadPastMessage && (
              <div className="maintenace-message past">
                We made some software updates, click refresh to see whats new.
                <Button
                  size="sm"
                  className="reload"
                  onClick={() => {
                    localStorage.setItem(
                      'payaca-pastMaintenaceMessageUpTimeAt',
                      maintenanceSchedule.past.upTimeAt
                    );
                    window.location.replace(window.location.href);
                  }}
                >
                  Refresh
                </Button>
              </div>
            )}
            {maintenanceSchedule?.future && !hasUnreadPastMessage && (
              <div className="maintenace-message future">
                We have scheduled maintenance from{' '}
                <span className="font-bold">
                  {maintenanceSchedule.future.downTimeAt}
                </span>{' '}
                until{' '}
                <span className="font-bold">
                  {maintenanceSchedule.future.upTimeAt}
                </span>{' '}
                in which our site will be unavailable. We are sorry for any
                inconvenience, have a nice day.
                <Button
                  className="dismiss"
                  variant="link"
                  onClick={() => {
                    if (maintenanceSchedule?.future) {
                      dispatch(
                        appActions.setLastReadFutureMaintenaceMessageDownTimeAt(
                          maintenanceSchedule.future.downTimeAt
                        )
                      );
                    }
                    dispatch(appActions.hideMaintenanceBanner(true));
                  }}
                >
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default MaintenanceBanner;
